import { FC } from 'react';
import { FolderOpen } from 'lucide-react';
import { CheckIcon } from 'components/icons/Icons';
import { ExclamationPointIcon } from 'components/icons/customIcons';
import GenericBadge from 'components/ui/Badge';
import { GenericColors } from 'enums/genericColors';
import { formatEuroPrice } from 'features/subscription-process/home-insurance/utils/realEstateFormatters';
import { useQuoteContractualizationData } from '../../store';

/**
 * Onglet « Information du parc » de la modale risque GLI ou CBNO (mode parc) :
 * synthèse du parc (libellé, numéro, nombre de biens couverts) et liste des biens
 * avec l'état de leur dossier (locataires en GLI, questionnaire du bien en CBNO).
 * En CBNO, tous les biens du parc sont listés : ceux hors contrat sont marqués et atténués.
 */
export const RiskParc: FC = () => {
  const quoteData = useQuoteContractualizationData();

  const parc = quoteData?.parc;
  const biens = quoteData?.parcRealEstates ?? [];

  if (!parc) {
    return <></>;
  }

  // Completeness only counts the properties covered by the contract.
  const coveredBiens = biens.filter(bien => bien.covered !== false);
  const completedCount = coveredBiens.filter(bien => bien.isCompleted).length;
  const coveredCount = parc.coveredCount ?? parc.realEstateCount;

  return (
    <div className="space-y-4">
      {/* Synthèse du parc */}
      <div className="bg-white rounded-xl p-4 shadow-sm border">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <FolderOpen className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1">
            <p className="font-semibold text-zinc-800">{parc.label}</p>
            <p className="text-sm text-gray-500 font-mono">{parc.parcNumber}</p>
          </div>
          <div className="text-right text-sm">
            <p className="font-medium text-zinc-800">
              {coveredCount} bien{coveredCount > 1 ? 's' : ''} couvert{coveredCount > 1 ? 's' : ''}
            </p>
            <p
              className={
                coveredBiens.length > 0 && completedCount === coveredBiens.length
                  ? 'text-green-600'
                  : 'text-orange-600'
              }
            >
              {completedCount}/{coveredBiens.length} biens complets
            </p>
          </div>
        </div>
      </div>

      {/* Liste des biens du parc */}
      <div className="space-y-2">
        {biens.map(bien => (
          <div
            key={bien.id}
            className={`flex items-center justify-between bg-white rounded-xl p-4 shadow-sm border ${
              bien.covered === false ? 'opacity-60' : ''
            }`}
          >
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {bien.isCompleted ? (
                <div className="p-1 rounded-full bg-green-500 text-white shrink-0">
                  <CheckIcon className="w-3 h-3" />
                </div>
              ) : (
                <div className="p-1 rounded-full bg-orange-500 text-white shrink-0">
                  <ExclamationPointIcon className="w-3 h-3" />
                </div>
              )}
              <div className="min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <p className="font-medium text-zinc-800 truncate">{bien.name}</p>
                  {bien.covered === false && (
                    <GenericBadge color={GenericColors.Gray} label="Hors contrat" badgeType="sm-bold" className="shrink-0" />
                  )}
                </div>
                {bien.address && (
                  <p className="text-sm text-gray-500 truncate">
                    {[bien.address.line1, bien.address.zipCode, bien.address.city]
                      .filter(Boolean)
                      .join(' ')}
                  </p>
                )}
              </div>
            </div>
            <div className="text-right text-sm shrink-0 ml-4">
              <p className="font-medium text-zinc-800">
                {formatEuroPrice(bien.rentWithCharges ?? 0)}
              </p>
              <p className="text-gray-500">Loyer CC</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
